import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e", goldL:"#d4a853",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  orange:"#d97706", orangeL:"rgba(217,119,6,0.10)",
  purple:"#7c3aed", purpleL:"rgba(124,58,237,0.10)",
  teal:"#0d9488", tealL:"rgba(13,148,136,0.10)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

export function TreatmentAnalytics() {
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("revenue");
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  
  useEffect(() => {
    const load = async () => {
      const fetchAll = async (table) => {
        const all = [];
        let from = 0;
        const PAGE = 1000;
        while (true) {
          const { data } = await supabase.from(table).select("*").range(from, from + PAGE - 1);
          if (!data || data.length === 0) break;
          all.push(...data);
          if (data.length < PAGE) break;
          from += PAGE;
        }
        return all;
      };
      const [a, c] = await Promise.all([fetchAll("appointments"), fetchAll("clients")]);
      setAppointments(a.filter(x => x.status === "completed" || x.id >= 5000000));
      setClients(c);
      setLoading(false);
    };
    load();
  }, []);
  
  // Aggregate by treatment
  const treatmentStats = {};
  appointments.forEach(a => {
    const treat = (a.treat || "Unknown").trim();
    if (!treat || treat === "Unknown") return;
    
    if (!treatmentStats[treat]) {
      treatmentStats[treat] = {
        name: treat,
        count: 0,
        revenue: 0,
        clients: new Set(),
        prices: [],
        dates: [],
        recentDates: [],
      };
    }
    
    treatmentStats[treat].count++;
    treatmentStats[treat].revenue += parseFloat(a.price) || 0;
    if (a.cid) treatmentStats[treat].clients.add(a.cid);
    treatmentStats[treat].prices.push(parseFloat(a.price) || 0);
    treatmentStats[treat].dates.push(a.date);
    
    // Recent (last 90 days)
    if (a.date) {
      const days = Math.floor((new Date() - new Date(a.date)) / 86400000);
      if (days <= 90) treatmentStats[treat].recentDates.push(a.date);
    }
  });
  
  // Calculate metrics
  const treatments = Object.values(treatmentStats).map(t => {
    const avgPrice = t.count > 0 ? t.revenue / t.count : 0;
    const uniqueClients = t.clients.size;
    const repeatRate = uniqueClients > 0 ? (t.count - uniqueClients) / t.count * 100 : 0;
    const recentCount = t.recentDates.length;
    const olderCount = t.count - recentCount;
    
    // Trend: comparing recent 90 days vs earlier 90 days
    const trend = recentCount > olderCount * 0.3 ? "up" : recentCount < olderCount * 0.15 ? "down" : "stable";
    
    return {
      name: t.name,
      count: t.count,
      revenue: t.revenue,
      avgPrice,
      uniqueClients,
      repeatRate,
      recentCount,
      trend,
    };
  });
  
  // Sort
  const sorted = {
    revenue: [...treatments].sort((a, b) => b.revenue - a.revenue),
    count: [...treatments].sort((a, b) => b.count - a.count),
    avg: [...treatments].sort((a, b) => b.avgPrice - a.avgPrice),
    repeat: [...treatments].sort((a, b) => b.repeatRate - a.repeatRate),
    trending: [...treatments].sort((a, b) => b.recentCount - a.recentCount),
  };
  
  // Total stats
  const totalRevenue = treatments.reduce((s, t) => s + t.revenue, 0);
  const totalSales = treatments.reduce((s, t) => s + t.count, 0);
  const uniqueTreatments = treatments.length;
  
  // Find clients of selected treatment
  const treatmentClients = selectedTreatment 
    ? appointments
        .filter(a => a.treat === selectedTreatment)
        .reduce((acc, a) => {
          const cid = a.cid;
          if (!cid) return acc;
          if (!acc[cid]) {
            const c = clients.find(x => x.id === cid);
            acc[cid] = {
              id: cid,
              name: c?.name || a.cn || "Unknown",
              email: c?.email || "",
              phone: c?.phone || "",
              count: 0,
              total: 0,
              dates: []
            };
          }
          acc[cid].count++;
          acc[cid].total += parseFloat(a.price) || 0;
          acc[cid].dates.push(a.date);
          return acc;
        }, {})
    : {};
  const topClients = Object.values(treatmentClients).sort((a, b) => b.total - a.total).slice(0, 20);
  
  // AI Insights
  const generateAIInsights = async () => {
    setLoadingAI(true);
    
    const top10 = sorted.revenue.slice(0, 10);
    const trending = sorted.trending.slice(0, 5);
    const declining = treatments.filter(t => t.trend === "down" && t.count >= 5).sort((a, b) => b.revenue - a.revenue).slice(0, 5);
    
    const summary = `
TOP 10 BY REVENUE:
${top10.map((t, i) => `${i+1}. ${t.name}: $${Math.round(t.revenue)} (${t.count} sales, avg $${Math.round(t.avgPrice)})`).join("\n")}

TRENDING (last 90 days):
${trending.map(t => `- ${t.name}: ${t.recentCount} recent sales`).join("\n")}

DECLINING:
${declining.map(t => `- ${t.name}: ${t.count} total but only ${t.recentCount} recent`).join("\n")}

TOTAL: ${totalSales} treatments, $${Math.round(totalRevenue)} revenue, ${uniqueTreatments} different services.
    `.trim();
    
    const prompt = `You are a med spa business consultant analyzing Nova Med Spa data.

DATA:
${summary}

Provide actionable insights in this exact JSON format:
{
  "topInsight": "Single most important insight in 1 sentence",
  "opportunities": [
    "Opportunity 1 - specific action",
    "Opportunity 2 - specific action",
    "Opportunity 3 - specific action"
  ],
  "warnings": [
    "Warning 1 if any",
    "Warning 2 if any"
  ],
  "actions": [
    "Specific marketing action 1",
    "Specific marketing action 2",
    "Specific marketing action 3"
  ]
}

Be specific. Use actual treatment names and dollar amounts. Focus on Med Spa industry best practices. Return ONLY the JSON, no other text.`;

    try {
      const { data } = await supabase.functions.invoke("generate-winback", {
        body: { 
          client: { name: "Business Insights", daysSince: 0, spent: 0, visits: 0 },
          offer: 0,
          tone: "analyst",
          customPrompt: prompt
        }
      });
      
      let text = data?.message || "";
      console.log("AI raw response:", text);
      
      // Aggressive JSON extraction
      const jsonStart = text.indexOf("{");
      const jsonEnd = text.lastIndexOf("}");
      if (jsonStart >= 0 && jsonEnd > jsonStart) {
        const jsonStr = text.substring(jsonStart, jsonEnd + 1);
        try {
          const parsed = JSON.parse(jsonStr);
          setAiInsights(parsed);
        } catch (e) {
          console.error("JSON parse failed:", e);
          setAiInsights({ topInsight: "Parse error - check console", opportunities: [], warnings: [], actions: [] });
        }
      } else {
        setAiInsights({ topInsight: text || "AI analysis unavailable", opportunities: [], warnings: [], actions: [] });
      }
    } catch (err) {
      console.error("AI failed:", err);
      // Fallback to static insights
      const top = sorted.revenue[0];
      const trending = sorted.trending[0];
      setAiInsights({
        topInsight: top ? `${top.name} is your top earner at $${Math.round(top.revenue)} - this is your golden goose.` : "Insufficient data",
        opportunities: [
          top ? `Push ${top.name} more - it has $${Math.round(top.avgPrice)} avg price` : "",
          trending ? `${trending.name} is trending - capitalize on momentum` : "",
          sorted.avg[0] ? `${sorted.avg[0].name} has highest avg ($${Math.round(sorted.avg[0].avgPrice)}) - target VIPs` : "",
        ].filter(Boolean),
        warnings: [],
        actions: [
          "Create package deals combining top 2-3 services",
          "Email past Botox clients about touch-ups (every 3-4 months)",
          "Target high-spend clients with VIP exclusive treatments",
        ]
      });
    }
    setLoadingAI(false);
  };
  
  if (loading) return <div style={{padding:40,textAlign:"center",color:C.muted}}>Loading treatment data...</div>;
  
  if (selectedTreatment) {
    const t = treatments.find(x => x.name === selectedTreatment);
    return (
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button onClick={()=>setSelectedTreatment(null)} style={{padding:"6px 12px",background:"#fff",color:C.muted,border:"1px solid "+C.border,borderRadius:6,cursor:"pointer",fontSize:12}}>← Back</button>
          <div>
            <div style={{fontSize:20,fontWeight:800,color:C.gold,fontFamily:S}}>{selectedTreatment}</div>
            <div style={{fontSize:11,color:C.muted}}>${Math.round(t.revenue).toLocaleString()} revenue · {t.count} sales · {t.uniqueClients} unique clients</div>
          </div>
        </div>
        
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10}}>
          <KPI label="Revenue" value={"$"+Math.round(t.revenue).toLocaleString()} color={C.gold} icon="💰"/>
          <KPI label="Sales" value={t.count} color={C.blue} icon="🛒"/>
          <KPI label="Avg Price" value={"$"+Math.round(t.avgPrice)} color={C.green} icon="📊"/>
          <KPI label="Repeat Rate" value={Math.round(t.repeatRate)+"%"} color={C.purple} icon="🔁"/>
        </div>
        
        <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden"}}>
          <div style={{padding:"14px 18px",borderBottom:"1px solid "+C.border,background:C.goldBg}}>
            <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>🏆 Top Clients for {selectedTreatment}</div>
            <div style={{fontSize:11,color:C.muted}}>Sorted by total spent on this treatment</div>
          </div>
          <div style={{maxHeight:500,overflowY:"auto"}}>
            {topClients.map((c, i) => (
              <div key={c.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderBottom:"1px solid "+C.border}}>
                <div style={{width:30,textAlign:"center",fontSize:14,fontWeight:800,color:i===0?C.gold:i<3?C.goldD:C.muted,fontFamily:S}}>
                  {i===0?"🥇":i===1?"🥈":i===2?"🥉":"#"+(i+1)}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:600,color:C.text}}>{c.name}</div>
                  <div style={{fontSize:11,color:C.muted}}>{c.email || c.phone || "—"}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:14,fontWeight:800,color:C.gold,fontFamily:S}}>${Math.round(c.total).toLocaleString()}</div>
                  <div style={{fontSize:10,color:C.muted}}>{c.count}× purchased</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div>
        <div style={{fontSize:22,fontWeight:800,color:C.gold,fontFamily:S}}>💎 Treatment Analytics</div>
        <div style={{fontSize:12,color:C.muted,marginTop:2}}>Revenue insights & smart recommendations across all services</div>
      </div>
      
      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12}}>
        <KPI label="Total Revenue" value={"$"+Math.round(totalRevenue).toLocaleString()} color={C.gold} icon="💰"/>
        <KPI label="Total Sales" value={totalSales.toLocaleString()} color={C.blue} icon="🛒"/>
        <KPI label="Services Offered" value={uniqueTreatments} color={C.green} icon="💎"/>
        <KPI label="Top Service" value={sorted.revenue[0]?.name?.slice(0, 20) || "—"} color={C.purple} icon="🏆" sub={sorted.revenue[0] ? "$"+Math.round(sorted.revenue[0].revenue).toLocaleString() : ""}/>
      </div>
      
      {/* AI Insights Box */}
      <div style={{padding:18,background:C.tealL,border:"1.5px solid "+C.teal,borderRadius:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div style={{fontSize:15,fontWeight:700,color:C.teal,fontFamily:S}}>🤖 AI Business Insights</div>
          {!aiInsights && <button disabled={loadingAI} onClick={generateAIInsights} style={{padding:"7px 14px",background:loadingAI?C.dim:C.teal,color:"#fff",border:"none",borderRadius:7,cursor:loadingAI?"wait":"pointer",fontSize:12,fontWeight:700}}>{loadingAI?"Analyzing...":"🧠 Generate Insights"}</button>}
        </div>
        
        {aiInsights && typeof aiInsights === "object" && (aiInsights.opportunities || aiInsights.warnings || aiInsights.actions) ? (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{padding:"10px 12px",background:"#fff",borderRadius:8,fontSize:13,color:C.text,fontWeight:600,borderLeft:"3px solid "+C.teal}}>
              💡 {aiInsights.topInsight}
            </div>
            
            {aiInsights.opportunities?.length > 0 && (
              <div>
                <div style={{fontSize:11,fontWeight:700,color:C.green,marginBottom:6,textTransform:"uppercase"}}>📈 Opportunities</div>
                {aiInsights.opportunities.map((o, i) => <div key={i} style={{padding:"8px 12px",background:C.greenL,borderRadius:6,fontSize:12,color:C.text,marginBottom:4}}>✓ {o}</div>)}
              </div>
            )}
            
            {aiInsights.warnings?.length > 0 && (
              <div>
                <div style={{fontSize:11,fontWeight:700,color:C.red,marginBottom:6,textTransform:"uppercase"}}>⚠️ Warnings</div>
                {aiInsights.warnings.map((w, i) => <div key={i} style={{padding:"8px 12px",background:C.redL,borderRadius:6,fontSize:12,color:C.text,marginBottom:4}}>! {w}</div>)}
              </div>
            )}
            
            {aiInsights.actions?.length > 0 && (
              <div>
                <div style={{fontSize:11,fontWeight:700,color:C.gold,marginBottom:6,textTransform:"uppercase"}}>🎯 Action Items</div>
                {aiInsights.actions.map((a, i) => <div key={i} style={{padding:"8px 12px",background:C.goldBg,borderRadius:6,fontSize:12,color:C.text,marginBottom:4}}>→ {a}</div>)}
              </div>
            )}
            
            <button onClick={()=>{setAiInsights(null);}} style={{padding:"5px 10px",background:"transparent",color:C.teal,border:"1px solid "+C.teal,borderRadius:6,cursor:"pointer",fontSize:11,alignSelf:"flex-start"}}>Regenerate</button>
          </div>
        ) : aiInsights && aiInsights.topInsight ? (
          <div style={{padding:14,background:"#fff",borderRadius:8,fontSize:13,color:C.text,whiteSpace:"pre-wrap",lineHeight:1.6}}>
            {aiInsights.topInsight}
            <button onClick={()=>{setAiInsights(null);}} style={{display:"block",marginTop:12,padding:"5px 10px",background:"transparent",color:C.teal,border:"1px solid "+C.teal,borderRadius:6,cursor:"pointer",fontSize:11}}>Regenerate</button>
          </div>
        ) : !loadingAI && (
          <div style={{fontSize:12,color:C.muted}}>Click "Generate Insights" to get AI-powered business recommendations based on your data.</div>
        )}
      </div>
      
      {/* Sort tabs */}
      <div style={{display:"flex",gap:4,background:C.light,padding:3,borderRadius:9,border:"1px solid "+C.border,flexWrap:"wrap"}}>
        {[
          ["revenue","💰 Revenue"],
          ["count","🛒 Most Sold"],
          ["avg","📊 Highest Avg"],
          ["repeat","🔁 Best Repeat"],
          ["trending","📈 Trending"],
        ].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{padding:"7px 14px",fontSize:12,fontWeight:tab===id?700:500,color:tab===id?"#fff":C.muted,background:tab===id?C.gold:"transparent",borderRadius:7,border:"none",cursor:"pointer",fontFamily:F}}>{label}</button>
        ))}
      </div>
      
      {/* Treatment list */}
      <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden"}}>
        <div style={{padding:"12px 18px",borderBottom:"1px solid "+C.border,background:C.goldBg,display:"flex",justifyContent:"space-between"}}>
          <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S}}>
            {tab==="revenue"?"💰 Top by Revenue":tab==="count"?"🛒 Most Sold":tab==="avg"?"📊 Highest Avg Price":tab==="repeat"?"🔁 Best Repeat Rate":"📈 Trending Now"}
          </div>
          <div style={{fontSize:11,color:C.muted}}>{sorted[tab].length} treatments</div>
        </div>
        <div style={{maxHeight:600,overflowY:"auto"}}>
          {sorted[tab].slice(0, 50).map((t, i) => (
            <div key={t.name} onClick={()=>setSelectedTreatment(t.name)} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderBottom:"1px solid "+C.border,cursor:"pointer",transition:"background 0.15s"}} onMouseEnter={e=>e.currentTarget.style.background=C.light} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <div style={{width:30,textAlign:"center",fontSize:14,fontWeight:800,color:i===0?C.gold:i<3?C.goldD:C.muted,fontFamily:S,flexShrink:0}}>
                {i===0?"🥇":i===1?"🥈":i===2?"🥉":"#"+(i+1)}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.name}</div>
                <div style={{fontSize:11,color:C.muted,display:"flex",gap:8,flexWrap:"wrap"}}>
                  <span>🛒 {t.count} sold</span>
                  <span>👥 {t.uniqueClients} clients</span>
                  <span>💰 ${Math.round(t.avgPrice)} avg</span>
                  {t.trend === "up" && <span style={{color:C.green,fontWeight:600}}>📈 Trending</span>}
                  {t.trend === "down" && <span style={{color:C.red,fontWeight:600}}>📉 Declining</span>}
                </div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:15,fontWeight:800,color:C.gold,fontFamily:S}}>${Math.round(t.revenue).toLocaleString()}</div>
                {tab === "repeat" && <div style={{fontSize:10,color:C.purple,fontWeight:700}}>{Math.round(t.repeatRate)}% repeat</div>}
                {tab === "trending" && <div style={{fontSize:10,color:C.green,fontWeight:700}}>{t.recentCount} recent</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KPI({label,value,color,icon,sub}) {
  return (
    <div style={{padding:"16px 18px",background:"#fff",border:"1px solid "+C.border,borderRadius:11}}>
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
        <span style={{fontSize:18}}>{icon}</span>
        <span style={{fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</span>
      </div>
      <div style={{fontSize:22,fontWeight:800,color:color,fontFamily:S,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{value}</div>
      {sub&&<div style={{fontSize:10,color:C.dim,marginTop:2}}>{sub}</div>}
    </div>
  );
}
